import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { AlertCircle, RefreshCw, XIcon } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"

import { createRequestBank, deleteBankAccount, sendCodeVerify, sendCodeVerifyDelete } from "@/api"
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui"
import { EValidateUser } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { formatPhoneNumber } from "@/modules/helpers/utils"

// Types
interface FormData {
  otp: string
  retry: boolean
}

// Constants
const OTP_LENGTH = 6
const OTP_EXPIRY_SECONDS = 90

// Config type for OTP confirmation
export interface OTPConfirmConfig<_TData = any> {
  storageKey: string
  sendCodeFn: () => Promise<any>
  onSuccess: (otp: string) => void
  description: string
  mutationKey: string
  resendMutationKey: string
  invalidateQueries?: string[][]
  onError?: (error: any) => { message?: string }
}

interface ConfirmOTPProps<TData = any> {
  visible: boolean
  onClose: () => void
  onSuccess: (code: string) => void
  data: TData
  config: OTPConfirmConfig<TData>
}

// Custom hook for OTP form logic
const useOTPForm = () => {
  return useForm<FormData>({
    defaultValues: {
      otp: "",
      retry: true
    }
  })
}

// Custom hook for OTP confirmation
const useOTPConfirmation = <TData,>(
  config: OTPConfirmConfig<TData>,
  data: TData,
  onSuccess: (code: string) => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (otp: string) => {
      // Call config.onSuccess which performs the actual operation (e.g., delete bank)
      await config.onSuccess(otp)
      return otp
    },
    mutationKey: [config.mutationKey],
    onError: config.onError,
    async onSuccess(otp) {
      if (config.invalidateQueries) {
        for (const queryKey of config.invalidateQueries) {
          await queryClient.invalidateQueries({ queryKey })
        }
      }
      // Only call parent onSuccess after mutation succeeds
      onSuccess(otp)
    }
  })
}

// Custom hook for OTP resend
const useOTPResend = <TData,>(config: OTPConfirmConfig<TData>, onResendSuccess?: () => void) => {
  return useMutation({
    mutationFn: () => config.sendCodeFn(),
    mutationKey: [config.resendMutationKey],
    onSuccess() {
      const expiryTime = dayjs(Date.now()).add(OTP_EXPIRY_SECONDS, "seconds").toString()
      localStorage.setItem(config.storageKey, expiryTime)
      onResendSuccess?.()
    },
    onError() {
      return { message: "Không thể gửi lại OTP" }
    }
  })
}

// Custom hook for countdown timer
const useCountdown = (storageKey: string) => {
  const [seconds, setSeconds] = useState(0)
  const [isActive, setIsActive] = useState(false)

  // Load remaining time from localStorage on mount
  useEffect(() => {
    const savedExpiryTime = localStorage.getItem(storageKey)
    if (savedExpiryTime) {
      const expiryTime = dayjs(savedExpiryTime)
      const now = dayjs()
      const remainingSeconds = expiryTime.diff(now, "second")

      if (remainingSeconds > 0) {
        setSeconds(remainingSeconds)
        setIsActive(true)
      } else {
        setSeconds(0)
        setIsActive(false)
        localStorage.removeItem(storageKey)
      }
    }
  }, [storageKey])

  const start = useCallback(() => {
    const savedExpiryTime = localStorage.getItem(storageKey)
    if (savedExpiryTime) {
      const expiryTime = dayjs(savedExpiryTime)
      const now = dayjs()
      const remainingSeconds = expiryTime.diff(now, "second")
      setSeconds(remainingSeconds)
      setIsActive(true)
    }
  }, [storageKey])

  const reset = useCallback(() => {
    const savedExpiryTime = localStorage.getItem(storageKey)
    if (savedExpiryTime) {
      const expiryTime = dayjs(savedExpiryTime)
      const now = dayjs()
      const remainingSeconds = expiryTime.diff(now, "second")
      setSeconds(remainingSeconds)
      setIsActive(true)
    }
  }, [storageKey])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isActive) {
      interval = setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            setIsActive(false)
            localStorage.removeItem(storageKey)
            return 0
          }
          return prevSeconds - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isActive, storageKey])

  return { seconds, isActive, start, reset }
}

// Component for OTP input field
const OTPInput: React.FC<{
  control: any
  onSubmit: (data: FormData) => void
}> = ({ control, onSubmit }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, value: string, field: any) => {
    // Only allow digits
    const digit = value.replace(/[^0-9]/g, "")

    if (digit.length > 1) {
      // If pasting multiple digits, distribute them across boxes
      const digits = digit.slice(0, OTP_LENGTH).split("")
      const currentOtp = Array.from({ length: OTP_LENGTH }, (_, i) => (field.value || "")[i] || "")

      digits.forEach((d: string, i: number) => {
        const targetIndex = index + i
        if (targetIndex < OTP_LENGTH) {
          currentOtp[targetIndex] = d
        }
      })

      field.onChange(currentOtp.join(""))

      // Focus on the next empty box or last box
      const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1)
      inputRefs.current[nextIndex]?.focus()
    } else {
      // Single digit input
      const currentOtp = Array.from({ length: OTP_LENGTH }, (_, i) => (field.value || "")[i] || "")
      currentOtp[index] = digit
      field.onChange(currentOtp.join(""))

      // Auto-focus next input
      if (digit && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>, field: any) => {
    if (e.key === "Backspace") {
      const currentOtp = Array.from({ length: OTP_LENGTH }, (_, i) => (field.value || "")[i] || "")

      if (!currentOtp[index] && index > 0) {
        // If current box is empty, move to previous box
        inputRefs.current[index - 1]?.focus()
      }

      currentOtp[index] = ""
      field.onChange(currentOtp.join(""))
    } else if (e.key === "Enter" && (field.value || "").replace(/\s/g, "").length === OTP_LENGTH) {
      onSubmit({ otp: field.value, retry: true })
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  return (
    <Controller
      name="otp"
      control={control}
      render={({ field, fieldState }) => {
        const otpValue = field.value || ""
        const otpDigits = Array.from({ length: OTP_LENGTH }, (_, i) => otpValue[i] || "")

        return (
          <div className="space-y-2">
            <div className="flex gap-2 justify-center">
              {otpDigits.map((digit: string, index: number) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value, field)}
                  onKeyDown={(e) => handleKeyDown(index, e, field)}
                  className="w-[23%] aspect-square text-center text-2xl font-medium border border-gray-300 rounded-[30%] focus:border-primary focus:outline-none transition-colors bg-white"
                />
              ))}
            </div>
            {fieldState.error && (
              <p className="text-sm text-red-500 text-center">{fieldState.error.message}</p>
            )}
          </div>
        )
      }}
    />
  )
}

// Component for error display
const ErrorAlert: React.FC<{
  error: string | null
}> = ({ error }) => {
  if (!error) return null

  return (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  )
}

// Component for resend button with countdown
const ResendButton: React.FC<{
  countdown: { seconds: number; isActive: boolean; start: () => void; reset: () => void }
  isPending: boolean
  onResend: () => void
}> = ({ countdown, isPending, onResend }) => {
  const { seconds, isActive } = countdown
  const canResend = !isActive && !isPending

  const handleResend = () => {
    onResend()
    countdown.start()
  }

  return (
    <div className="flex justify-center">
      <Button
        variant="link"
        onClick={handleResend}
        disabled={!canResend}
        className="flex items-center gap-2"
      >
        {isPending && <RefreshCw className="h-4 w-4 animate-spin" />}
        {isActive ? `Gửi lại sau ${seconds}s` : isPending ? "Đang gửi..." : "Gửi lại"}
      </Button>
    </div>
  )
}

// Main generic component
export const ConfirmOTP = <TData,>({
  visible,
  onClose,
  onSuccess,
  data,
  config
}: ConfirmOTPProps<TData>) => {
  const [generalError, setGeneralError] = useState<string | null>(null)
  const { user } = useCurrentUser()
  const form = useOTPForm()
  const countdown = useCountdown(config.storageKey)

  const {
    mutate: confirmOTP,
    error: confirmError,
    isPending: isConfirmPending
  } = useOTPConfirmation(config, data, (code: string) => onSuccess(code))
  const {
    mutate: resendOTP,
    isPending: isResendPending,
    error: resendError
  } = useOTPResend(config, () => {
    setGeneralError(null)
    countdown.reset()
    form.clearErrors()
  })

  const handleSubmit = ({ otp }: FormData) => {
    if (otp?.length < OTP_LENGTH) {
      form.setError("otp", { message: "Nhập thiếu mã xác minh." })
      return
    }

    form.clearErrors()
    setGeneralError(null)
    confirmOTP(otp)
  }

  const handleResend = () => {
    setGeneralError(null)
    form.clearErrors()
    resendOTP()
    form.setValue("retry", true)
  }

  // Handle errors from mutations
  useEffect(() => {
    if (confirmError) {
      const error = confirmError as any
      const errorData = error?.response?.data
      const errorType = errorData?.error?.type

      let message = errorData?.error?.message

      if (errorType?.includes(EValidateUser.OTP_NOT_MATCHED)) {
        message = "Mã xác minh không đúng."
      }
      if (errorType?.includes(EValidateUser.OTP_EXPIRED)) {
        message = "Mã xác minh hết hạn."
      }
      if (errorType?.includes(EValidateUser.BALANCE_NOT_ENOUGH)) {
        message = "Số dư không đủ."
      }
      if (errorType?.includes(EValidateUser.YOU_CAN_ONLY_SEND_OTP_AFTER_60S)) {
        message = "Bạn chỉ có thể gửi mã xác minh sau 60 giây."
      }

      if (errorType?.includes(EValidateUser.BANK_ACCOUNT_EXISTED)) {
        message = "Tài khoản ngân hàng đã tồn tại."
      }
      setGeneralError(message)
    }
  }, [confirmError, form])

  // Handle resend errors
  useEffect(() => {
    if (resendError) {
      setGeneralError("Không thể gửi lại OTP. Vui lòng thử lại sau.")
    }
  }, [resendError])

  // Clear errors and start countdown when dialog opens
  useEffect(() => {
    if (visible) {
      form.clearErrors()
      setGeneralError(null)
      countdown.start() // Start countdown when dialog opens
    } else {
      countdown.reset() // Reset countdown when dialog closes
      form.reset()
    }
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={visible} onOpenChange={onClose} modal>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Xác nhận OTP</DialogTitle>
          <DialogClose onClick={onClose} className="absolute top-2 right-2">
            <XIcon className="w-6 h-6 text-muted-foreground" />
          </DialogClose>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {config.description.replace("{phone}", formatPhoneNumber(user?.phoneNumber) || "")}
            </p>
          </div>

          <ErrorAlert error={generalError} />

          <OTPInput control={form.control} onSubmit={handleSubmit} />

          <ResendButton countdown={countdown} isPending={isResendPending} onResend={handleResend} />

          <Button
            className="w-full h-14 rounded-full bg-gradient-primary"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isConfirmPending}
          >
            {isConfirmPending ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Đang xác nhận...
              </div>
            ) : (
              "Xác nhận"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Wrapper component for Add Bank
interface ConfirmOTPBankProps {
  visible: boolean
  onClose: () => void
  onSuccess: () => void
  data: {
    bankId: string
    accountNumber: string
    accountHolderName: string
    bankCode: string
    isDefault: boolean
  }
}

export const ConfirmOTPBank: React.FC<ConfirmOTPBankProps> = (props) => {
  const config: OTPConfirmConfig<ConfirmOTPBankProps["data"]> = {
    storageKey: "@expired_time_otp_bank",
    sendCodeFn: sendCodeVerify,
    onSuccess: async (otp: string) => {
      await createRequestBank({
        accountNumber: props.data.accountNumber,
        accountHolderName: props.data.accountHolderName,
        bankCode: props.data.bankCode,
        code: otp
      })
    },
    description:
      "Nhập mã OTP mà chúng tôi vừa gửi đến số điện thoại {phone} để yêu cầu thêm ngân hàng",
    mutationKey: "confirm_otp_Bank",
    resendMutationKey: "resend_otp_Bank",
    invalidateQueries: [["bank_account_list"]],
    onError: (err: any) => {
      const errorData = err?.response?.data
      const errorType = errorData?.error.type
      let message = errorData?.error?.message

      if (errorType?.includes(EValidateUser.OTP_NOT_MATCHED)) {
        message = "Mã xác minh không đúng."
      } else if (errorType?.includes(EValidateUser.OTP_EXPIRED)) {
        message = "Mã xác minh hết hạn."
      } else if (errorType?.includes(EValidateUser.BALANCE_NOT_ENOUGH)) {
        message = "Số dư không đủ."
      }

      return { message }
    }
  }

  return (
    <ConfirmOTP
      visible={props.visible}
      onClose={props.onClose}
      onSuccess={() => props.onSuccess()}
      data={props.data}
      config={config}
    />
  )
}

// Wrapper component for Delete Bank
interface ConfirmOTPDeleteBankProps {
  visible: boolean
  onClose: () => void
  onSuccess: (code: string) => void
  data: {
    bankId: string
  }
}

export const ConfirmOTPDeleteBank: React.FC<ConfirmOTPDeleteBankProps> = (props) => {
  const config: OTPConfirmConfig<ConfirmOTPDeleteBankProps["data"]> = {
    storageKey: "@expired_time_otp_delete_bank",
    sendCodeFn: sendCodeVerifyDelete,
    onSuccess: async (otp: string) => {
      // Call delete API with OTP code - this validates OTP and deletes the bank
      await deleteBankAccount(props.data.bankId, otp)
    },
    description:
      "Nhập mã OTP mà chúng tôi vừa gửi đến số điện thoại {phone} để xác nhận xóa tài khoản ngân hàng",
    mutationKey: "confirm_otp_delete_Bank",
    resendMutationKey: "resend_otp_delete_Bank",
    invalidateQueries: [["bank_account_list"]],
    onError: (err: any) => {
      const errorData = err?.response?.data
      const errorType = errorData?.error.type
      let message = errorData?.error?.message

      if (errorType?.includes(EValidateUser.OTP_NOT_MATCHED)) {
        message = "Mã xác minh không đúng."
      }
      if (errorType?.includes(EValidateUser.OTP_EXPIRED)) {
        message = "Mã xác minh hết hạn."
      }
      if (errorType?.includes(EValidateUser.BALANCE_NOT_ENOUGH)) {
        message = "Số dư không đủ."
      }
      if (errorType?.includes(EValidateUser.YOU_CAN_ONLY_SEND_OTP_AFTER_60S)) {
        message = "Bạn chỉ có thể gửi mã xác minh sau 60 giây."
      }

      if (errorType?.includes(EValidateUser.BANK_ACCOUNT_EXISTED)) {
        message = "Tài khoản ngân hàng đã tồn tại."
      }

      return { message }
    }
  }

  return <ConfirmOTP {...props} config={config} />
}
